/**
 * HTTP service for the hours feature (PX-W6.4): the seven timesheet endpoints
 * of `HoursController` (`server/conf/routes`, verified 2026-09-04). One
 * transport; keyed envelopes reject as `ApiError`.
 *
 * `/{orgId}` placeholders resolve on the shared transport from the session's
 * active org. Paths use the project SLUG and the entry's ObjectId HEX id.
 */

import { apiClient } from '@/services'

import type { HoursEntry, LogHoursInput, UpdateHoursInput } from '../types'

const base = (projectId: string): string => `/{orgId}/projects/${projectId}/hours`

export const hoursServiceHttp = {
  /** Every entry of a project (firm+client rows; no status param — filter presentational). */
  list(projectId: string): Promise<HoursEntry[]> {
    return apiClient.get<HoursEntry[]>(base(projectId))
  },

  /** Log a new entry — born `draft` on the server (`POST`, 201 → the row). */
  create(projectId: string, input: LogHoursInput): Promise<HoursEntry> {
    return apiClient.post<HoursEntry>(base(projectId), input)
  },

  /** Hand a draft in (`POST …/submit`); idempotent on `submitted`. */
  submit(projectId: string, entryId: string): Promise<HoursEntry> {
    return apiClient.post<HoursEntry>(`${base(projectId)}/${entryId}/submit`)
  },

  /** One entry. */
  get(projectId: string, entryId: string): Promise<HoursEntry> {
    return apiClient.get<HoursEntry>(`${base(projectId)}/${entryId}`)
  },

  /**
   * Full replace of the mutable fields (`PUT`) — a decision PUT echoes the
   * row's own `day`/`minutes`/`billable`/`note` and changes only `status`
   * (decision D-2). Omitted fields BLANK on the server; nothing is ever
   * omitted implicitly here.
   */
  update(projectId: string, entryId: string, input: UpdateHoursInput): Promise<HoursEntry> {
    return apiClient.put<HoursEntry>(`${base(projectId)}/${entryId}`, input)
  },

  /** Delete a draft of the caller's own (or any row, for an org-admin) — 204. */
  remove(projectId: string, entryId: string): Promise<void> {
    return apiClient.delete<void>(`${base(projectId)}/${entryId}`)
  },

  /** The caller's own entries across projects, newest first. */
  myHours(limit = 50, offset = 0): Promise<HoursEntry[]> {
    return apiClient.get<HoursEntry[]>('/{orgId}/my-hours', { params: { limit, offset } })
  }
}
